import { useEffect, useState } from "react";
import axiosInstance from "../../../../api/axios";

const AdminSchoolEmail = ({ email, disabled, onBusyChange, onDirtyChange, onVerified }) => {
  const [record, setRecord] = useState(null);
  const [schoolEmail, setSchoolEmail] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [message, setMessage] = useState("");
  const [retry, setRetry] = useState(0);
  const initialEmail = record?.pending_email || "";
  const dirty = !record?.verified && (schoolEmail !== initialEmail || Boolean(reason.trim()));

  useEffect(() => { onBusyChange(busy); }, [busy, onBusyChange]);
  useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");
    axiosInstance.get(`/api/school-email/admin/${encodeURIComponent(email)}`).then(({ data }) => {
      if (!active) return;
      setRecord(data);
      setSchoolEmail(data.pending_email || "");
      setReason("");
    }).catch(() => {
      if (active) setLoadError("Unable to load school-email verification.");
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [email, retry]);

  const verify = async event => {
    event.preventDefault();
    setBusy(true);
    setSaveError("");
    setMessage("");
    try {
      const { data } = await axiosInstance.post(
        `/api/school-email/admin/${encodeURIComponent(email)}`,
        { school_email: schoolEmail.trim(), reason: reason.trim() },
      );
      setRecord(data);
      setReason("");
      onVerified?.(data.email);
      setMessage("School email marked as verified.");
    } catch (error) {
      setSaveError(error.response?.data?.message || "Unable to verify this school email. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="vu-form-card vu-verification-card" aria-busy={loading || busy}>
      <h3 className="vu-item-label">School-email verification</h3>
      {loading ? <p role="status">Loading verification...</p> : loadError ? (
        <p role="alert">{loadError} <button type="button" onClick={() => setRetry(value => value + 1)}>Retry verification</button></p>
      ) : record?.verified ? (
        <>
          <p><strong>Verified:</strong> {record.email}</p>
          {record.verified_by && <p className="vu-helper-text">Manually verified by {record.verified_by}.</p>}
        </>
      ) : (
        <form onSubmit={verify}>
          <p className="vu-helper-text">Use this only after independently checking the applicant's student status. It does not confirm attendance or change their login email.</p>
          <fieldset className="vu-decision-fields" disabled={disabled || busy}>
            <label className="label-vu" htmlFor="admin-school-email">School email</label>
            <input id="admin-school-email" type="email" required maxLength={254} autoComplete="off"
              className="confirm-deadline" value={schoolEmail} onChange={event => setSchoolEmail(event.target.value)} />
            <label className="label-vu" htmlFor="admin-verification-reason">Verification reason</label>
            <textarea id="admin-verification-reason" required minLength={5} maxLength={500} rows={3}
              value={reason} onChange={event => setReason(event.target.value)}
              aria-describedby="admin-verification-note" />
            <p id="admin-verification-note" className="vu-helper-text">Record what you checked. Your admin account and this reason are saved. Gmail is not accepted as a school email.</p>
            <button type="submit" className="user-dash-b">{busy ? "Verifying..." : "Mark school email verified"}</button>
          </fieldset>
        </form>
      )}
      {message && <p className="vu-inline-message" role="status">{message}</p>}
      {saveError && <p className="vu-inline-error" role="alert">{saveError}</p>}
    </div>
  );
};

export default AdminSchoolEmail;
